import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, map, retry, tap, timeout } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

export interface LicenseCatalogEntry {
    code: string;
    label: string;
    order: number;
    colorKey: string;
}

// No hardcoded fallback list on purpose — a fixed list of "known" codes here would be exactly the
// problem this ticket removes, just moved into the unreachable-catalogue case (and QA caught it:
// a state that already has CNA_I saved rendered blank, because CNA_I wasn't in that list). When the
// catalogue is empty, getLicenseLabel falls back to the raw stored code and sortByLicenseOrder falls
// back to alphabetical — i.e. "show the stored codes as they are, in a neutral order" per the AC.
let cachedEntries: LicenseCatalogEntry[] = [];
const entriesSubject = new BehaviorSubject<LicenseCatalogEntry[]>([]);

/** Synchronous snapshot of the licence catalogue, for plain (non-injectable) helpers and functions. */
export function getLicenseCatalog(): LicenseCatalogEntry[] {
    return cachedEntries;
}

/** Emits whenever the catalogue (re)loads, for plain modules that can't inject LicenseCatalogService. */
export const licenseCatalog$: Observable<LicenseCatalogEntry[]> = entriesSubject.asObservable();

export function getLicenseEntry(code: string): LicenseCatalogEntry | undefined {
    const target = (code || '').toUpperCase();
    return cachedEntries.find(e => e.code && e.code.toUpperCase() === target);
}

export function getLicenseLabel(code: string): string {
    return getLicenseEntry(code)?.label ?? code;
}

export function getLicenseOrder(code: string): number {
    return getLicenseEntry(code)?.order ?? Number.MAX_SAFE_INTEGER;
}

// For a TSchema `style.getClasses`: the color comes from the catalogue's colorKey instead of a
// per-code CSS class, so a code the catalogue doesn't know about renders with no color class
// rather than needing a new CSS rule.
export function getLicenseColorClasses(code: string): Record<string, boolean> {
    const colorKey = getLicenseEntry(code)?.colorKey;
    return colorKey ? { [colorKey]: true } : {};
}

// Only used to break a tie when the catalogue doesn't know either code (including every code while
// it's unreachable) — higher wins, same convention as the catalogue's own `order`. RN and LPN ranking
// above everything else is the one seniority fact every state/client variant this codebase has ever
// encoded agreed on universally; the aide tier below them genuinely varies by state and has no safe
// default, so it's left to alphabetical. This matters beyond display: getNurseHighestAvailableLicense
// feeds a facility rate-table lookup, so picking an aide-tier code over RN for a nurse who holds both
// would price a shift off the wrong rate band while the catalogue is down, not just show the wrong badge.
const DEGRADED_SENIORITY: Record<string, number> = { RN: 2, LPN: 1 };

export function sortByLicenseOrder<T>(items: T[], getCode: (item: T) => string, direction: 'asc' | 'desc' = 'asc'): T[] {
    const factor = direction === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
        const orderDiff = factor * (getLicenseOrder(getCode(a)) - getLicenseOrder(getCode(b)));
        if (orderDiff !== 0) return orderDiff;

        const seniorityDiff = factor * (
            (DEGRADED_SENIORITY[(getCode(a) || '').toUpperCase()] ?? 0) -
            (DEGRADED_SENIORITY[(getCode(b) || '').toUpperCase()] ?? 0)
        );
        if (seniorityDiff !== 0) return seniorityDiff;

        return factor * (getCode(a) || '').localeCompare(getCode(b) || '');
    });
}

@Injectable({ providedIn: 'root' })
export class LicenseCatalogService {
    private readonly endpoint = environment.apiUrl;
    private started = false;

    // The live catalogue, not a one-shot snapshot of the first attempt — so a caller that awaits
    // this also sees a later successful retry after an initial failure, not a cached empty result.
    readonly entries$ = entriesSubject.asObservable();

    constructor(private readonly http: HttpClient) { }

    load(): Observable<LicenseCatalogEntry[]> {
        if (!this.started) {
            this.started = true;
            this.fetch().subscribe();
        }

        return this.entries$;
    }

    // On failure, degrades immediately (empty catalogue, handled by every getLicenseXxx fallback) but
    // keeps retrying with backoff, capped at 5 minutes between attempts and ~3 hours total, so a
    // transient outage (or the backend catalogue not being deployed to this environment yet) self-heals
    // without the admin needing to manually reload the page. Uses RxJS's own retry/delay rather than a
    // hand-rolled timer().subscribe() chain, so the whole retry sequence is one subscription that stops
    // cleanly if the caller ever unsubscribes, instead of a dangling, uncancellable timer.
    private fetch(): Observable<LicenseCatalogEntry[]> {
        return this.http.get<{ licenses: LicenseCatalogEntry[] }>(`${this.endpoint}/licenses/public`).pipe(
            timeout(8000),
            map(response => response.licenses),
            tap(entries => {
                cachedEntries = entries;
                entriesSubject.next(entries);
            }),
            retry({
                count: 40,
                delay: (_, retryCount) => {
                    // Only emit on the first failure — it's a real transition subscribers need to react
                    // to (unknown -> known-empty). Repeated failures while retrying haven't changed
                    // anything, so re-emitting would just re-trigger every subscriber's rebuild logic
                    // (e.g. the facilities form's rate-limits rebuild) on every retry tick for no reason.
                    if (retryCount === 1) {
                        cachedEntries = [];
                        entriesSubject.next([]);
                    }

                    return timer(Math.min(30000 * retryCount, 300000));
                }
            }),
            catchError(() => of([] as LicenseCatalogEntry[]))
        );
    }
}
